import { FC, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { Box, Button } from '@qwealth/qcore';
import { S3Item } from '@qwealth/qvault';
import { fetchRTQListWithQIDs, loadLegalEntity, RTQ } from '@qwealth/qdata';
import {
  AddressesInfoConfirmation,
  BankingInfoConfirmation,
  EmploymentInfoConfirmation,
  FinancialInfoConfirmation,
  GovernmentIdConfirmation,
  LegalEntityBasicInfo,
  LegalEntityOwnershipInfo,
  RegulatoryInfoConfirmation,
  useAddressInfo,
  useDemographicInfo,
  useFinancialInfo,
  useGovernmentInfo,
  useRegulatoryInfo,
} from '@qwealth/kyc';

import useMandates from 'hooks/useMandates';
import DemographicInfo from 'components/Contacts/ContactDetails/VitalTab/DemographicInfo';
import CitizenshipAndResidency from 'components/Contacts/ContactDetails/VitalTab/CitizenshipAndResidency';
import { getSectionForShortCode } from 'utils/ComplianceUtil';
import { useAppSelector } from 'data/store';
import { selectClientComplianceReview } from 'data/selectors/compliance';
import {
  ClientComplianceReview,
  COMPLIANCE_CLIENT_RTQ_LEVEL_CORPORATE,
  COMPLIANCE_CLIENT_RTQ_LEVEL_HOUSEHOLD,
  COMPLIANCE_CLIENT_RTQ_LEVEL_INDIVIDUAL,
  ShortCode,
} from 'data/models/Compliance';
import { loadSingleContact } from 'data/actions/contacts';
import PMNotesCard from './PMNotesCard';
import Definitions from './Definitions';
import PDFViewer from '../PDFViewer/PdfViewer';
import DocumentSelection from '../PDFViewer/DocumentSelection';
import ComplianceQVault from './ComplianceQVault';
import MessagesNotesHistorySection from './MessagesNotesHistorySection';
import ClientNotesHistoryModal from '../Common/NotesHistory/ClientNotesHistoryModal';

const selectDocumentShortCodes = [
  'welcome_letter',
  'referral_disclosure',
  'all_ima_agreement_sigs',
  'all_ima_fee_sigs',
  'partner_office',
  'household_set_up',
  'rep_code_match',
  'ips_check',
  'ips_completed',
  'ips_language',
  'investment_restriction',
  'ips_signed',
  'advising_rep_name',
  'all_fields_populated',
];

const taskSectionTypes = ['PDFViewer', 'SelectDocument', 'QVault', 'Messages'];

const ComplianceTasksCard: FC = (): JSX.Element => {
  const selectedClient: ClientComplianceReview | undefined = useAppSelector(selectClientComplianceReview);
  const [isLegalEntity, setIsLegalEntity] = useState<boolean>(false);
  const [taskToDisplay, setTaskToDisplay] = useState<ShortCode>('Messages');
  const [showNotesHistoryModal, setShowNotesHistoryModal] = useState<boolean>(false);

  const legalEntity = useAppSelector(state =>
    state.legalEntities && state.legalEntities.legalEntityInitialized ? state.legalEntities.legalEntity : undefined,
  );
  const { governmentInfo } = useGovernmentInfo(selectedClient?.QID);
  const { financialInfo } = useFinancialInfo(selectedClient?.QID);
  const { demographicInfo } = useDemographicInfo(selectedClient?.QID ? selectedClient?.QID : '');
  const { addressesInfo } = useAddressInfo(selectedClient?.QID as string);
  const { regulatoryInfo } = useRegulatoryInfo(selectedClient?.QID as string);
  const [selectedContactRTQ, setSelectedContactRTQ] = useState<RTQ | undefined>();
  const [pdfToDisplay, setPdfToDisplay] = useState<S3Item>();
  const [searchParams] = useSearchParams();
  const mandateMap = useMandates();
  const section = searchParams.get('section') as ShortCode;
  const dispatch = useDispatch();

  const changeTaskItem = (changeTo: ShortCode) => {
    if (taskSectionTypes.includes(changeTo)) {
      setTaskToDisplay(changeTo);
    } else if (selectDocumentShortCodes.includes(changeTo)) {
      setTaskToDisplay('SelectDocument');
    } else {
      setTaskToDisplay('All Contact Info');
    }
  };

  useEffect(() => {
    const rtqLevel = selectedClient?.rtqLevel;
    const isCorporate = rtqLevel === 'Corporate';
    setIsLegalEntity(isCorporate);

    if (rtqLevel && selectedClient) {
      const loadClient = isCorporate ? loadLegalEntity : loadSingleContact;
      dispatch(loadClient(selectedClient.QID));
    }

    getAndSetRTQ();
  }, [selectedClient]);

  useEffect(() => {
    changeTaskItem(section);
    const sectionToScrollTo = getSectionForShortCode(section, isLegalEntity);
    handleScrollTo(sectionToScrollTo as keyof typeof sectionRefs);
  }, [section]);

  const getRtqFromRtqArrayMap = (qidToRtqArrayMap: Record<string, RTQ[]>): RTQ | undefined => {
    if (selectedClient && selectedClient.household.QID) {
      return qidToRtqArrayMap[selectedClient.household.QID].find((rtq: RTQ | undefined) => {
        if (!rtq) return false;

        const isActive = rtq.isActive === 1;
        const hasMatchingLevel = rtq.latestActiveRtqLevel === selectedClient.rtqLevel;

        if (!isActive || !hasMatchingLevel) return false;

        if ([COMPLIANCE_CLIENT_RTQ_LEVEL_INDIVIDUAL, COMPLIANCE_CLIENT_RTQ_LEVEL_CORPORATE].includes(selectedClient.rtqLevel)) {
          return rtq.QID === selectedClient.QID;
        }

        if (selectedClient.rtqLevel === COMPLIANCE_CLIENT_RTQ_LEVEL_HOUSEHOLD) {
          return true;
        }

        return false; // Don't select rtq with no level
      });
    }
  };

  const getAndSetRTQ = async () => {
    if (selectedClient?.household.QID !== undefined && selectedClient?.QID !== undefined) {
      const qidToRTQArrayMap = await fetchRTQListWithQIDs(selectedClient?.household.QID, [selectedClient.QID]);
      const rtq = getRtqFromRtqArrayMap(qidToRTQArrayMap);
      setSelectedContactRTQ(rtq);
    }
  };

  const sectionRefs = {
    GovernmentId: useRef<HTMLDivElement>(null),
    DemographicInfo: useRef<HTMLDivElement>(null),
    LegalEntityInfo: useRef<HTMLDivElement>(null),
    EmploymentInfo: useRef<HTMLDivElement>(null),
    FinancialInfo: useRef<HTMLDivElement>(null),
    Regulatory: useRef<HTMLDivElement>(null),
    PMNotes: useRef<HTMLDivElement>(null),
    RTQResponses: useRef<HTMLDivElement>(null),
    Definitions: useRef<HTMLDivElement>(null),
  };

  const handleScrollTo = (section: keyof typeof sectionRefs) => {
    sectionRefs[section]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const getBankingInfo = () => {
    if (isLegalEntity && legalEntity !== undefined) {
      return legalEntity.nbinBankAccountDetails || [];
    }

    return demographicInfo.nbinBankAccountDetails || [];
  };

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        width="50%"
        height="100vh"
        backgroundColor="white"
        padding="large"
        boxShadow="layout"
        marginBottom="large"
      >
        <Box display="flex" flexDirection="column" gap="default" marginBottom="large">
          <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
            <Box fontSize="appTitle" fontWeight="bold">
              {taskToDisplay}
            </Box>
            {taskToDisplay === 'Messages' && (
              <Button
                onClick={() => {
                  setShowNotesHistoryModal(true);
                }}
              >
                View Notes History
              </Button>
            )}
          </Box>
          <Box height="1px" backgroundColor="gray.0" />
        </Box>
        {taskToDisplay === 'SelectDocument' && <DocumentSelection setPdfToDisplay={setPdfToDisplay} changeTo={changeTaskItem} />}

        {taskToDisplay === 'QVault' && <ComplianceQVault />}

        {taskToDisplay === 'PDFViewer' && (
          <Box minWidth="100%">
            <PDFViewer defaultPdf={pdfToDisplay} />
          </Box>
        )}

        {taskToDisplay === 'Messages' && <MessagesNotesHistorySection contactQID={selectedClient?.QID} />}

        {taskToDisplay === 'All Contact Info' && (
          <Box overflowY="scroll">
            {isLegalEntity && legalEntity ? (
              <Box ref={sectionRefs.LegalEntityInfo}>
                <LegalEntityBasicInfo legalEntity={legalEntity} />
                <LegalEntityOwnershipInfo ownership={legalEntity?.ownership || []} />
              </Box>
            ) : (
              selectedClient?.QID &&
              selectedClient?.household.QID && (
                <Box ref={sectionRefs.DemographicInfo}>
                  <DemographicInfo contactQID={selectedClient.QID} householdQID={selectedClient?.household.QID} isCompliance={true} />
                  <CitizenshipAndResidency isEditDisabled={true} contactQID={selectedClient.QID} />
                </Box>
              )
            )}
            <AddressesInfoConfirmation addressesInfo={addressesInfo} />
            <Box ref={sectionRefs.Regulatory}>
              <RegulatoryInfoConfirmation regulatoryInfo={regulatoryInfo} context={isLegalEntity ? 'Contact' : 'Entity'} />
            </Box>
            {!isLegalEntity && (
              <Box>
                <Box ref={sectionRefs.EmploymentInfo}>
                  <EmploymentInfoConfirmation contactQid={selectedClient?.QID as string} />
                </Box>
                <Box ref={sectionRefs.GovernmentId}>
                  <GovernmentIdConfirmation
                    governmentInfo={governmentInfo}
                    additionalFields={{
                      verificationMethod: { label: 'Verification Method' },
                    }}
                  />
                </Box>
              </Box>
            )}

            <Box>
              <BankingInfoConfirmation
                bankingInfo={{
                  nbinBankAccountDetails: getBankingInfo(),
                }}
              />
            </Box>
            <Box ref={sectionRefs.FinancialInfo}>
              <FinancialInfoConfirmation financialInfo={financialInfo} />
            </Box>
            <Box ref={sectionRefs.PMNotes} marginTop="largest" fontSize="appTitle" fontWeight="bold">
              PM Notes & RTQ Responses
            </Box>
            <Box ref={sectionRefs.RTQResponses}>
              {selectedContactRTQ && (
                <Box>
                  <Box display="flex" flexDirection="row" gap="largest" marginTop="large">
                    <Box>
                      <Box fontWeight="bold">Suggested Mandate</Box>
                      {selectedContactRTQ.mandate}
                    </Box>

                    <Box>
                      <Box fontWeight="bold">PM Mandate</Box>
                      {mandateMap[selectedContactRTQ.pmMandate ?? '']}
                    </Box>
                  </Box>

                  <PMNotesCard rtq={selectedContactRTQ} hasTabs={false} isEntity={isLegalEntity} />
                </Box>
              )}
            </Box>
            <Box marginTop="largest"></Box>

            <Box as="div" ref={sectionRefs.Definitions}>
              <Definitions />
            </Box>
          </Box>
        )}
      </Box>
      <ClientNotesHistoryModal
        contactQID={selectedClient?.QID}
        contactName={selectedClient?.name}
        checklistStatus={selectedClient?.complianceChecklistStatus}
        isComplianceApproved={false}
        show={showNotesHistoryModal}
        onClose={() => setShowNotesHistoryModal(false)}
      />
    </>
  );
};

export default ComplianceTasksCard;
